/* Copyright (c) 2017 Backpack Health LLC */

/* global document */
import * as _ from 'lodash';

import DateUtil from '../utilities/DateUtil';
import ImageUtil from '../utilities/ImageUtil';

class PhotoModel {

  static createFromRawFile(rawFile, autoOrient, callback) {
    let fileName = rawFile.name || '';
    if (fileName !== '') {
      fileName = fileName.match(/(.*)(?=\.\w+$)/)[0];
    }

    const photoModel = new PhotoModel({
      name: fileName,
      mime_type: rawFile.type,
      byte_length: rawFile.size,
      fileData: rawFile,
      observed: {
        date: DateUtil.today(),
        precision: 'P1D',
      },
    });

    const imageToDataUrlFunc = autoOrient ?
      ImageUtil.autoOrientImage :
      ImageUtil.imageToDataUrl;

    imageToDataUrlFunc(rawFile, imageUrl => {
      photoModel.data.dataUrl = imageUrl;
      ImageUtil.imageFromDataUrl(imageUrl, image => {
        photoModel.data.image = image;
        callback(photoModel);
      });
    });
  }

  constructor(data = {}) {
    this.data = _.assign({}, {

    }, data);
  }

  restrictToMaxWidth(maxWidth, callback) {
    if (maxWidth < this.data.image.width) {
      return this.resizeImage(maxWidth, (rawData) => {
        PhotoModel.createFromRawFile(rawData, false, callback);
      });
    }
    return callback(this);
  }

  resizeImage(width, callback) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = canvas.width * (this.data.image.height / this.data.image.width);
    ctx.drawImage(this.data.image, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      callback(blob);
    }, this.data.mime_type, 0.75);
  }

  cropToSquare(callback) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const squareSide = Math.min(this.data.image.height, this.data.image.width);
    canvas.width = squareSide;
    canvas.height = squareSide;
    ctx.drawImage(this.data.image, 0, 0, squareSide, squareSide, 0, 0, squareSide, squareSide);
    canvas.toBlob((blob) => {
      callback(blob);
    }, this.data.mime_type, 0.75);
  }

  isDimensionTooSmall() {
    return this.data.image.width < 300 || this.data.image.height < 300;
  }

  isDimensionTooLarge() {
    return this.data.image.width > 1200 || this.data.image.height > 1200;
  }

  isSquareImage() {
    return this.data.image.width === this.data.image.height;
  }

  getName() {
    return _.get(this, 'data.name', '');
  }

  getMimeType() {
    return _.get(this, 'data.mime_type', '');
  }

  fileName() {
    return this.data.name;
  }

  setName(name) {
    this.data.name = name;
  }

  isImage() {
    return /image/.test(this.getMimeType());
  }

  fileSizeData() {
    let size = _.get(this, 'data.byte_length', 0);
    let unit = 'b';

    const sizeKiloBytes = size / 1000;
    const sizeMegaBytes = sizeKiloBytes / 1000;
    const sizeGigaBytes = sizeMegaBytes / 1000;
    const sizeTeraBytes = sizeGigaBytes / 1000;

    if (sizeTeraBytes >= 1) {
      size = sizeTeraBytes;
      unit = 'TB';
    } else if (sizeGigaBytes >= 1) {
      size = sizeGigaBytes;
      unit = 'GB';
    } else if (sizeMegaBytes >= 1) {
      size = sizeMegaBytes;
      unit = 'MB';
    } else if (sizeKiloBytes >= 1) {
      size = sizeKiloBytes;
      unit = 'kB';
    }
    size = Number(size).toFixed(2);
    size = size % 1 === 0 ? parseInt(Math.floor(size), 10) : parseFloat(size, 10);

    return {
      size,
      unit,
    };
  }

  getByteSize() {
    return _.get(this, 'data.byte_length', 0);
  }

  formatObservedDate() {
    const dateData = this.getObservedDate();
    return DateUtil.formattedMediumDate(dateData.date, dateData.precision);
  }

  setDate(dateData) {
    this.data.observed = _.assign({}, this.data.observed, dateData);
  }

}

export default PhotoModel;
